<!--
  WidgetsPurchasePaymentBucket.vue

  Single-bucket sibling of WidgetsExpensePaymentBucket /
  WidgetsAdminCostPaymentBucket, with currency awareness. Each payment
  row carries its own SupplierCurrency / ForeignAmount / ExchangeRate /
  LocalAmount. Multi-currency is allowed per Stock — different payments
  may use different currencies (vehicle in JPY + shipping in USD).

  The header total shows the MYR sum (always meaningful, since all
  LocalAmount values are in MYR). A secondary line below shows per-
  currency foreign-amount subtotals when multiple currencies are present.

  In the Add/Edit modal, LocalAmount auto-recalculates as
  ForeignAmount × ExchangeRate whenever either changes — but the field
  stays editable so users can override for bank fees, negotiated rates,
  or rounding deltas.

  Props:
    title           - bucket heading (e.g. "Purchase Payments")
    payments        - array of PurchasePayment rows
    defaultCurrency - pre-fills the Currency dropdown for new payments
    isReadOnly      - hides Add/Edit/Delete buttons when true

  Emits:
    add    -> payload for stocks/AddPurchasePayment/{stockId}
    update -> { id, payload } for stocks/UpdatePurchasePayment/{id}
    remove -> paymentId for stocks/RemovePurchasePayment/{id}
-->
<template>
  <CCard class="mb-3">
    <CCardHeader class="d-flex justify-content-between align-items-center py-2">
      <strong>{{ title }}</strong>
      <span class="text-right">
        <strong class="text-primary">Total: RM {{ formattedLocalTotal }}</strong>
        <div v-if="hasMultipleCurrencies" class="small text-muted">
          {{ foreignSubtotalsLine }}
        </div>
      </span>
    </CCardHeader>

    <CCardBody class="py-2">
      <CDataTable
        :items="payments || []"
        :fields="fields"
        small
        striped
        :noItemsView="{ noItems: 'No payments recorded' }"
      >
        <template #paymentDate="{ item }">
          <td>{{ displayDate(item.paymentDate) }}</td>
        </template>

        <template #foreignAmount="{ item }">
          <td class="text-right">
            {{ item.supplierCurrency || '' }} {{ formatMoney(item.foreignAmount) }}
          </td>
        </template>

        <template #exchangeRate="{ item }">
          <td class="text-right">{{ formatRate(item.exchangeRate) }}</td>
        </template>

        <template #localAmount="{ item }">
          <td class="text-right">RM {{ formatMoney(item.localAmount) }}</td>
        </template>

        <template #referenceNo="{ item }">
          <td><small>{{ item.referenceNo || '-' }}</small></td>
        </template>

        <template #document="{ item }">
          <td>
            <a
              v-if="item.document"
              :href="documentUrl(item.document)"
              target="_blank"
              :title="item.document.fileName"
            >
              {{ truncateFileName(item.document.fileName) }}
            </a>
            <small v-else class="text-muted">-</small>
          </td>
        </template>

        <template #actions="{ item }">
          <td class="py-1">
            <CButton
              v-if="!isReadOnly"
              size="sm"
              color="info"
              class="mr-1"
              @click="openEditModal(item)"
            >
              Edit
            </CButton>
            <CButton
              v-if="!isReadOnly"
              size="sm"
              color="danger"
              @click="$emit('remove', item.id)"
            >
              Delete
            </CButton>
          </td>
        </template>
      </CDataTable>
    </CCardBody>

    <CCardFooter v-if="!isReadOnly" class="py-2">
      <CButton size="sm" color="primary" @click="openAddModal">
        <i class="fas fa-plus"></i> Add Payment
      </CButton>
    </CCardFooter>

    <CModal
      :title="(editingId ? 'Edit' : 'Add') + ' Payment - ' + title"
      :show.sync="showModal"
      size="lg"
      :closeOnBackdrop="false"
    >
      <CForm>
        <CInput
          label="Payment Date"
          type="date"
          v-model="form.paymentDate"
          horizontal
        />

        <CSelect
          label="Supplier Currency"
          :options="currencyOptions"
          :value.sync="form.supplierCurrency"
          horizontal
        />

        <CInput
          label="Foreign Amount"
          :prepend="form.supplierCurrency || ''"
          type="number"
          step="0.01"
          v-model.number="form.foreignAmount"
          horizontal
        />

        <CInput
          label="Exchange Rate"
          type="number"
          step="0.000001"
          v-model.number="form.exchangeRate"
          horizontal
          description="Rate to convert to MYR (e.g. 0.033 for JPY, 4.7 for USD). Local = Foreign × Rate."
        />

        <CInput
          label="Local Amount"
          prepend="RM"
          type="number"
          step="0.01"
          v-model.number="form.localAmount"
          horizontal
          readonly
          description="Auto-calculated from Foreign × Rate."
        />

        <CInput
          label="Reference No"
          v-model="form.referenceNo"
          horizontal
          description="Cheque #, bank ref, receipt #, etc."
        />

        <CSelect
          label="Payment Method"
          :options="methodOptions"
          :value.sync="form.paymentMethod"
          horizontal
        />

        <CInput
          label="Remarks"
          v-model="form.remarks"
          horizontal
        />

        <CRow class="mb-3">
          <CCol sm="3" class="text-sm-right">
            <label class="col-form-label">Receipt</label>
          </CCol>
          <CCol sm="9">
            <WidgetsUploadDocument
              :key="uploadKey"
              @uploaded="onDocumentUploaded"
              :multiple="false"
            />
            <div v-if="form.document" class="mt-2">
              <small class="text-success">
                <i class="fas fa-check-circle"></i>
                {{ form.document.fileName || 'Receipt uploaded' }}
              </small>
              <CButton
                size="sm"
                color="danger"
                variant="ghost"
                @click="clearDocument"
                class="ml-2"
              >
                <i class="fas fa-times"></i> Remove
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="showModal = false">Cancel</CButton>
        <CButton color="primary" @click="onSaveClicked">
          {{ editingId ? 'Update' : 'Save' }}
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import WidgetsUploadDocument from './WidgetsUploadDocument.vue'
import { formatMalaysiaDate } from '@/utils/dateUtil'

const CURRENCY_OPTIONS = [
  { value: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'THB', label: 'THB — Thai Baht' },
  { value: 'CNY', label: 'CNY — Chinese Yuan' }
]

const METHOD_OPTIONS = [
  { value: '', label: '(select)' },
  { value: 'Cash', label: 'Cash' },
  { value: 'BankTransfer', label: 'Bank Transfer' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Other', label: 'Other' }
]

const FIELDS = [
  { key: 'paymentDate', label: 'Date' },
  { key: 'foreignAmount', label: 'Foreign', _classes: 'text-right' },
  { key: 'exchangeRate', label: 'Rate', _classes: 'text-right' },
  { key: 'localAmount', label: 'Local (RM)', _classes: 'text-right' },
  { key: 'referenceNo', label: 'Reference' },
  { key: 'document', label: 'Receipt' },
  { key: 'actions', label: '', _style: 'width: 130px' }
]

export default {
  name: 'WidgetsPurchasePaymentBucket',
  components: { WidgetsUploadDocument },
  props: {
    title: { type: String, required: true },
    payments: { type: Array, default: () => [] },
    defaultCurrency: { type: String, default: 'MYR' },
    isReadOnly: { type: Boolean, default: false }
  },
  data () {
    return {
      currencyOptions: CURRENCY_OPTIONS,
      methodOptions: METHOD_OPTIONS,
      fields: FIELDS,
      showModal: false,
      editingId: null,
      uploadKey: 0,
      form: this.emptyForm()
    }
  },
  computed: {
    localTotal () {
      if (!Array.isArray(this.payments)) return 0
      return this.payments.reduce((s, p) => s + (Number(p.localAmount) || 0), 0)
    },
    formattedLocalTotal () {
      return this.formatMoney(this.localTotal)
    },
    foreignSubtotals () {
      // Group foreign amounts by currency. Returns a map { JPY: 1500000, USD: 500 }
      const grouped = {}
      for (const p of (this.payments || [])) {
        const cur = (p.supplierCurrency || '').trim() || '?'
        grouped[cur] = (grouped[cur] || 0) + (Number(p.foreignAmount) || 0)
      }
      return grouped
    },
    hasMultipleCurrencies () {
      return Object.keys(this.foreignSubtotals).length > 1
    },
    foreignSubtotalsLine () {
      return Object.entries(this.foreignSubtotals)
        .map(([cur, sum]) => `${cur} ${this.formatMoney(sum)}`)
        .join(' · ')
    }
  },
  watch: {
    // Auto-recalculate LocalAmount whenever ForeignAmount or
    // ExchangeRate changes. The field stays editable so the user can
    // still override afterwards if needed.
    'form.foreignAmount' () {
      this.recalcLocalAmount()
    },
    'form.exchangeRate' () {
      this.recalcLocalAmount()
    }
  },
  methods: {
    emptyForm () {
      return {
        paymentDate: new Date().toISOString().slice(0, 10),
        supplierCurrency: this.defaultCurrency || 'MYR',
        foreignAmount: 0,
        exchangeRate: 1,
        localAmount: 0,
        referenceNo: '',
        paymentMethod: '',
        remarks: '',
        documentId: null,
        document: null
      }
    },
    recalcLocalAmount () {
      const f = Number(this.form.foreignAmount) || 0
      const r = Number(this.form.exchangeRate) || 0
      // Round to 2 decimal places for MYR currency. Use Math.round to
      // avoid floating-point trailing digits like 49500.00000000001.
      this.form.localAmount = Math.round(f * r * 100) / 100
    },
    formatMoney (n) {
      const v = Number(n) || 0
      return v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatRate (n) {
      const v = Number(n) || 0
      // Show up to 6 decimal places so small JPY-style rates (0.033)
      // are legible without truncating to 0.03.
      return v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
    },
    formatDate (iso) {
      if (!iso) return ''
      return String(iso).slice(0, 10)
    },
    displayDate (iso) {
      return formatMalaysiaDate(iso)
    },
    truncateFileName (name) {
      if (!name) return 'View'
      if (name.length <= 24) return name
      const dot = name.lastIndexOf('.')
      const ext = dot > -1 ? name.slice(dot) : ''
      const head = name.slice(0, 18 - ext.length)
      return head + '…' + ext
    },
    documentUrl (doc) {
      if (!doc) return '#'
      const apiBase = (window.config && window.config.API_URL) || ''
      const root = apiBase.replace(/\/api\/?$/, '')
      return root + '/api/Documents/File/' + doc.id
    },
    openAddModal () {
      this.editingId = null
      this.form = this.emptyForm()
      this.uploadKey++
      this.showModal = true
    },
    openEditModal (payment) {
      this.editingId = payment.id
      this.form = {
        paymentDate: this.formatDate(payment.paymentDate),
        supplierCurrency: payment.supplierCurrency || this.defaultCurrency || 'MYR',
        foreignAmount: Number(payment.foreignAmount) || 0,
        exchangeRate: Number(payment.exchangeRate) || 1,
        localAmount: Number(payment.localAmount) || 0,
        referenceNo: payment.referenceNo || '',
        paymentMethod: payment.paymentMethod || '',
        remarks: payment.remarks || '',
        documentId: payment.documentId || null,
        document: payment.document || null
      }
      this.uploadKey++
      this.showModal = true
    },
    onDocumentUploaded (data) {
      if (data && Array.isArray(data.uploadedFiles) && data.uploadedFiles.length > 0) {
        const first = data.uploadedFiles[0]
        this.form.document = first
        this.form.documentId = first.id
      } else {
        this.form.document = null
        this.form.documentId = null
      }
    },
    clearDocument () {
      this.form.document = null
      this.form.documentId = null
      this.uploadKey++
    },
    onSaveClicked () {
      this.commit()
      this.showModal = false
    },
    buildPayload () {
      return {
        supplierCurrency: this.form.supplierCurrency || 'MYR',
        foreignAmount: Number(this.form.foreignAmount) || 0,
        exchangeRate: Number(this.form.exchangeRate) || 0,
        localAmount: Number(this.form.localAmount) || 0,
        paymentDate: this.form.paymentDate
          ? new Date(this.form.paymentDate).toISOString()
          : null,
        referenceNo: this.form.referenceNo || null,
        paymentMethod: this.form.paymentMethod || null,
        remarks: this.form.remarks || null,
        documentId: this.form.documentId || null
      }
    },
    commit () {
      const payload = this.buildPayload()
      if (this.editingId) {
        this.$emit('update', { id: this.editingId, payload })
      } else {
        this.$emit('add', payload)
      }
      this.editingId = null
    }
  }
}
</script>
